import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, Zap, Copy, ExternalLink } from 'lucide-react'

interface ZapModalProps {
    isOpen: boolean
    onClose: () => void
    recipientName: string
    lnAddress?: string
}

export function ZapModal({ isOpen, onClose, recipientName, lnAddress }: ZapModalProps) {
    const [copied, setCopied] = useState(false)

    const handleCopy = () => {
        if (lnAddress) {
            navigator.clipboard.writeText(lnAddress)
            setCopied(true)
            setTimeout(() => setCopied(false), 2000)
        }
    }

    if (!isOpen) return null

    return (
        <AnimatePresence>
            <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
                <motion.div
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.9, opacity: 0 }}
                    className="bg-white dark:bg-surface-900 rounded-3xl shadow-2xl max-w-md w-full overflow-hidden border border-surface-200 dark:border-surface-700"
                >
                    {/* Header */}
                    <div className="p-6 border-b border-surface-100 dark:border-surface-800 flex items-center justify-between bg-gradient-to-r from-orange-50 to-surface-50 dark:from-orange-900/10 dark:to-surface-800">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-orange-500/10 rounded-xl flex items-center justify-center">
                                <Zap className="w-5 h-5 text-orange-500" />
                            </div>
                            <div>
                                <h2 className="font-poppins font-bold text-lg text-gray-900 dark:text-white">Bahşiş Gönder</h2>
                                <p className="text-xs text-gray-500 dark:text-gray-400">Bitcoin İşlemi</p>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-2 hover:bg-surface-100 dark:hover:bg-surface-800 rounded-lg transition-colors"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    {/* Content */}
                    <div className="p-6 space-y-4">
                        <div className="text-center space-y-2">
                            <p className="text-sm text-gray-600 dark:text-gray-400 font-poppins">
                                <span className="font-bold text-gray-900 dark:text-white">{recipientName}</span> kullanıcısına bahşiş göndermek için:
                            </p>
                        </div>

                        {lnAddress ? (
                            <>
                                <div className="p-4 bg-surface-50 dark:bg-surface-800/50 rounded-2xl border border-surface-200 dark:border-surface-700">
                                    <p className="text-xs font-poppins font-bold text-gray-400 uppercase tracking-widest mb-2">Bitcoin Adresi</p>
                                    <div className="flex items-center gap-2">
                                        <code className="flex-1 text-sm font-mono text-gray-900 dark:text-white break-all">
                                            {lnAddress}
                                        </code>
                                        <button
                                            onClick={handleCopy}
                                            className="p-2 hover:bg-surface-100 dark:hover:bg-surface-700 rounded-lg transition-colors flex-shrink-0"
                                            title="Kopyala"
                                        >
                                            <Copy className="w-4 h-4 text-gray-500" />
                                        </button>
                                    </div>
                                    {copied && (
                                        <motion.p
                                            initial={{ opacity: 0, y: -5 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            className="text-xs text-green-600 dark:text-green-400 mt-2 font-poppins font-bold"
                                        >
                                            ✓ Kopyalandı!
                                        </motion.p>
                                    )}
                                </div>

                                <a
                                    href={`lightning:${lnAddress}`}
                                    className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-br from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white rounded-2xl font-poppins font-bold text-sm shadow-lg shadow-orange-500/20 transition-all"
                                >
                                    <Zap className="w-4 h-4" />
                                    Cüzdan ile Aç
                                    <ExternalLink className="w-3.5 h-3.5" />
                                </a>
                            </>
                        ) : (
                            <div className="text-center py-8 space-y-3">
                                <div className="w-16 h-16 bg-surface-100 dark:bg-surface-800 rounded-full flex items-center justify-center mx-auto">
                                    <Zap className="w-8 h-8 text-gray-300 dark:text-gray-600" />
                                </div>
                                <p className="text-sm text-gray-500 dark:text-gray-400 font-poppins">
                                    Bu kullanıcı henüz bahşiş adresi belirtmemiş.
                                </p>
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="p-4 bg-surface-50/50 dark:bg-surface-800/30 border-t border-surface-100 dark:border-surface-800">
                        <p className="text-[10px] text-center text-gray-400 dark:text-gray-500 font-poppins font-bold">
                            Bitcoin ile anında ve düşük ücretli bahşiş göndererek içeriği destekleyebilirsiniz.
                        </p>
                    </div>
                </motion.div>
            </div>
        </AnimatePresence>
    )
}
